/**
 * Shared reservation pricing calculator.
 *
 * As of Iter 26 the agency enters UNIT prices in the form (per oda per gece,
 * per kişi per gün) instead of pre-computed totals. This module centralises
 * the arithmetic so the wizard, single-page özel acente form, detail page
 * and operasyon approval queue all agree on the same total.
 *
 * Formulas (per user's specification):
 *   - room_total  = room_unit_price × total_rooms × nights
 *   - meal_total  = (meal_unit_price / 2) × total_meals × adults
 *       * user enters meal price for ONE FULL DAY (2 öğün); we divide by 2
 *         to get the per-meal unit price.
 *       * `adults` only — children + görevli are EXCLUDED from meal cost
 *         per user's explicit instruction.
 *       * `total_meals` depends on meal_start / meal_end and nights:
 *           - guest stays (nights+1) days total
 *           - day 1: 2 meals if meal_start == kahvalti, else 1
 *           - middle days (2..nights): 2 meals each
 *           - last day (nights+1): 2 meals if meal_end == aksam, else 1
 *   - grand_total = room_total + meal_total
 *
 * All inputs are coerced to safe numbers; NaN/negative → 0.
 */

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v || 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function totalRoomsFromDist(rooms) {
  if (!rooms || typeof rooms !== "object") return 0;
  return (rooms.r1 || 0) + (rooms.r2 || 0) + (rooms.r3 || 0) + (rooms.r4 || 0) + (rooms.r5 || 0);
}

/** Compute the number of billable meals for the stay based on start/end
 *  markers and the number of nights. Assumes exactly 2 meals per full day
 *  (kahvaltı + akşam). Returns 0 for a same-day stay (nights <= 0). */
export function totalMealCount({ meal_start, meal_end, nights }) {
  const n = Math.max(0, Math.floor(num(nights)));
  if (n <= 0) return 0;
  const start = String(meal_start || "kahvalti").toLowerCase();
  const end   = String(meal_end   || "aksam").toLowerCase();
  // Day 1
  let day1 = start === "kahvalti" ? 2 : 1;
  // Days 2..nights (i.e. `nights - 1` middle days)
  const middle = Math.max(0, n - 1) * 2;
  // Last day (index n+1). If meal_end == aksam → had breakfast + dinner → 2;
  // if kahvalti → only breakfast → 1.
  const last  = end === "aksam" ? 2 : 1;
  return day1 + middle + last;
}

/**
 * Compute everything. Input shape:
 *   { rooms, nights, adults, children, staff, meal_start, meal_end,
 *     room_unit_price, meal_unit_price }
 * Output shape:
 *   { totalRooms, totalMeals, mealPersons, perMealPrice,
 *     roomTotal, mealTotal, grandTotal }
 */
export function computeReservationTotal(input) {
  const totalRooms = totalRoomsFromDist(input?.rooms);
  const nights = Math.max(0, Math.floor(num(input?.nights)));
  const adults = Math.max(0, Math.floor(num(input?.adults)));   // meal-billable
  const roomUnit = num(input?.room_unit_price);
  const mealUnit = num(input?.meal_unit_price);
  const totalMeals = totalMealCount({
    meal_start: input?.meal_start,
    meal_end: input?.meal_end,
    nights,
  });
  const perMealPrice = mealUnit / 2;
  const roomTotal = roomUnit * totalRooms * nights;
  const mealTotal = perMealPrice * totalMeals * adults;
  const grandTotal = roomTotal + mealTotal;
  return {
    totalRooms, totalMeals,
    mealPersons: adults,
    perMealPrice,
    roomTotal, mealTotal,
    grandTotal,
  };
}
